/**
 * @author Nihar<nihar.baishya@314ecorp.com>
 * @description utils for claim status fhir
 */

import _ from 'lodash';
import { getAddress, getContacts, getHumanName, getIdentifierMeta } from '../eligibility/fhir';
import { labelMapping } from 'app-constants/eligibility/fhirLabelMapping';
import { renderDate } from 'components/eligibility/response/RenderSchema';
import fhirSystemMapping from 'app-constants/eligibility/fhirSystemMapping.json';
import { formatDate } from 'utils/eligibility/eligibilityBatchUpload';
import { getDisplayOrCode } from './claimRequest';
import { CLAIM_TYPES } from 'app-constants/claims/claimRequest';
import { getCareTeamMeta as getProfessionalCareTeamMeta } from './professionalClaimRequestParser';
import { extractModifiers } from './getClaimLineLevelInformation';
import { formatAmount } from 'utils/accounts/allAccounts';
import { EMPTY_VALUE } from '@veritable/common/constants';
import { getClaimResponseFinancialMeta } from './claimInformation';

// code mapping for various fields
const codeMapping = {
	submittedAmount: 'submitted',
	benefitAmount: 'benefit',
	serviceDate: 'service-performed',
};

const CLAIM_STATUS_CODING_SYSTEMS = [
	fhirSystemMapping['EXTENSION-507'],
	fhirSystemMapping['EXTENSION-508'],
	fhirSystemMapping['STC-STC01-01'],
	fhirSystemMapping['STC-STC01-02'],
];

const renderAmount = (amount?: number): string => {
	return amount ? formatAmount(amount) : EMPTY_VALUE;
};

// function to abstract  resource details
const getResourceDetails = (resource: AnyType): ClaimStatusResponse.ItemType[] => {
	const resourceDetails = [] as ClaimStatusResponse.ItemType[];
	const address = resource?.address ?? [];
	const telecom = resource?.telecom ?? [];
	const contact = resource?.contact ?? [];
	const identifier = resource?.identifier ?? ([] as Fhir.Identifier[]);
	const type = resource?.type?.[0]?.coding?.[0]?.display;

	// type
	if (type) {
		resourceDetails.push({
			label: labelMapping.type,
			value: type,
		});
	}

	// name
	if (resource?.name) {
		if (_.isString(resource?.name)) {
			resourceDetails.push({
				label: labelMapping.name,
				value: resource?.name,
			});
		} else {
			const nameMeta = getHumanName(resource?.name || []);
			resourceDetails.push(
				{
					label: labelMapping.firstName,
					value: nameMeta?.firstName,
				},
				{
					label: labelMapping.lastName,
					value: nameMeta?.lastName,
				},
				{
					label: labelMapping.name,
					value: nameMeta?.name,
				},
			);
		}
	}
	// gender
	if (resource?.gender) {
		resourceDetails.push({
			label: labelMapping.gender,
			value: _.startCase(resource?.gender),
		});
	}
	// birth date
	if (resource?.birthDate) {
		resourceDetails.push({
			label: labelMapping.dob,
			value: renderDate(resource?.birthDate),
		});
	}

	// for patient type of resource
	if (!_.isEmpty(address)) {
		resourceDetails.push({
			label: labelMapping.address,
			value: getAddress(_.first(address) || {}),
		});
	}
	if (!_.isEmpty(telecom)) {
		resourceDetails.push({
			label: labelMapping.contact,
			value: getContacts([{ telecom }]),
		});
	}
	// for organization types of resource
	if (!_.isEmpty(contact)) {
		const addressDetail = _.find(contact, (obj) => obj?.address ?? {});
		if (!_.isEmpty(addressDetail)) {
			resourceDetails.push(
				{
					label: labelMapping.address,
					value: getAddress(addressDetail?.address as Fhir.Address),
				},
				{
					label: labelMapping.contact,
					value: getContacts((contact as Fhir.Contacts[]) || []),
				},
			);
		}
	}
	const identifierMeta = getIdentifierMeta(identifier);
	return _.concat(resourceDetails, identifierMeta);
};

const getTrnNumber = (traceNumber?: Fhir.Identifier[]): ClaimStatusResponse.ItemType[] => {
	return getIdentifierMeta(traceNumber || []);
};

const getAdjudicationItems = (adjudicationData?: Fhir.Extension[]): ClaimStatusResponse.ItemType[] => {
	const items = [] as ClaimStatusResponse.ItemType[];
	_.forEach(adjudicationData, (extension) => {
		const codingList = extension?.valueCodeableConcept?.coding ?? [];
		_.map(codingList, ({ system, code, display }) => {
			if (_.includes(CLAIM_STATUS_CODING_SYSTEMS, system) && (code || display)) {
				items.push({
					label: code,
					value: display || '',
				});
			}
		});
	});
	return items;
};
const getTotalAmount = (total?: Fhir.ClaimTotal[]): ClaimStatusResponse.ItemType[] => {
	const items = [] as ClaimStatusResponse.ItemType[];
	_.forEach(total, (item) => {
		const codingList = item?.category?.coding || [];
		_.forEach(codingList, ({ code }) => {
			if (code === codeMapping.submittedAmount || code === codeMapping.benefitAmount) {
				items.push({
					label: code === codeMapping.submittedAmount ? labelMapping.billedAmount : labelMapping.paidAmount,
					value: renderAmount(item?.amount.value),
				});
			}
		});
	});
	return items;
};

const getPaymentDetails = (data?: Fhir.ClaimResponse): ClaimRequest.IPaymentData => {
	const payment = data?.payment;
	const paymentData: Partial<ClaimRequest.IPaymentData> = {};

	if (payment?.amount?.value) {
		paymentData.amount = {
			value: payment.amount.value,
			currency: payment.amount.currency || 'USD',
		};
	}

	if (payment?.date) {
		paymentData.date = payment.date;
	}
	if (payment?.identifier?.system === fhirSystemMapping['STC-STC09']) {
		const remittanceNumber = payment.identifier.value;
		const remittancePeriod = payment.identifier.period;

		if (remittanceNumber || remittancePeriod) {
			paymentData.identifier = {
				system: payment.identifier.system,
				value: remittanceNumber || '',
			};

			if (remittancePeriod) {
				paymentData.identifier.period = {
					start: remittancePeriod.start,
					end: remittancePeriod.end || remittancePeriod.start,
				};
			}
		}
	}

	const financialMeta = getClaimResponseFinancialMeta(data);

	return {
		...financialMeta,
		...paymentData,
	};
};

// TODO: not used anymore, remove if not required in future
const getPaymentDetailsFromAllocation = (
	claimId: string,
	paymentAllocationData: ClaimStatusResponse.IERAPaymentReconciliationModel,
): ClaimRequest.IPaymentData => {
	const claimResponse = paymentAllocationData?.fhirClaimResponse;
	const paymentReconciliation = paymentAllocationData?.fhirResource;

	const claimsPaymentData = getPaymentDetails(claimResponse);

	const paymentData: Partial<ClaimRequest.IPaymentData> = {};

	const allocation = _.find(paymentReconciliation?.allocation, ({ target }) => target?.reference === `Claim/${claimId}`);
	if (allocation) {
		paymentData.amount = {
			value: allocation?.amount?.value,
			currency: allocation?.amount?.currency || 'USD',
		};
		paymentData.date = paymentReconciliation?.date;
		const identifier = _.first(paymentReconciliation?.identifier);
		if (identifier) {
			paymentData.identifier = {
				system: identifier.system,
				value: identifier.value,
			};
		}
	}

	const financialMeta = getClaimResponseFinancialMeta(claimResponse);

	return {
		...financialMeta,
		...claimsPaymentData,
		...paymentData,
	};
};

const getAdjudicationData = (adjudicationData: Fhir.IClaimResponseItemAdjudication[]): ClaimStatusResponse.IAdjudicationItem => {
	const adjudication = {
		allowedAmount: 0,
		paidAmount: 0,
	};

	const items = [] as ClaimStatusResponse.IAdjudicationLineItem[];
	_.forEach(adjudicationData, ({ category, reason, amount }) => {
		const group = _.find(category?.coding, { system: fhirSystemMapping['CAS-01'] });
		const reasonCode = _.find(reason?.coding, { system: fhirSystemMapping['CAS-02'] });
		const allowedAmount = _.find(category?.coding, { system: fhirSystemMapping['AMT-01'], code: 'B6' });

		const paidAmount = _.find(category?.coding, { system: fhirSystemMapping.AdjudicationCodes, code: 'benefit' });

		if (allowedAmount) {
			adjudication.allowedAmount = amount?.value;
		}

		if (paidAmount) {
			adjudication.paidAmount = amount?.value;
		}

		if (group?.code && reasonCode?.code) {
			items.push({
				group: {
					value: group?.code,
					label: group?.display,
				},
				reasonCode: {
					value: reasonCode?.code,
					label: reasonCode?.display,
				},
				amount: amount?.value,
			});
		}
	});

	return {
		...adjudication,
		lineItems: items,
	};
};

const getRevenueCode = (item: Fhir.ClaimItem | Fhir.ClaimResponseAddItem): Fhir.Coding => {
	return _.find(item?.revenue?.coding, (revenueCodeItem) => revenueCodeItem?.system === fhirSystemMapping?.RevenueCode);
};

const getHcpcsCode = (item: Fhir.ClaimItem | Fhir.ClaimResponseAddItem): Fhir.Coding => {
	return _.find(item?.productOrService?.coding, (coding) => coding?.system === fhirSystemMapping.hcpcsCode);
};

// function to get line level information
const getLineLevelInformation = (data: ClaimRequest.IClaim): ClaimStatusResponse.ILineLevelInformation[] => {
	// claim response data
	const infoList: ClaimStatusResponse.ILineLevelInformation[] = [];
	// claim response data
	const claimResponseData = data?.fhirResource;
	// TODO : later revert to data?.fhirClaimResponses?.x12_277
	const claimStatusData = data?.claimStatusRequestResponse?.fhirClaimResponse as Fhir.ClaimResponse;
	const eraData = data?.fhirClaimResponses?.x12_835 as Fhir.ClaimResponse;
	const manualPaymentData = data?.fhirClaimResponses?.post_manual as Fhir.ClaimResponse;

	const addItemsList = claimResponseData?.item || [];
	const claimType = data?.metadata?.claimType;

	let careTeam = {} as InstitutionalClaimParser.ICareTeamMeta | ProfessionalClaimParser.ICareTeamMeta;
	if (claimType === CLAIM_TYPES.professional.value) {
		careTeam = getProfessionalCareTeamMeta(data);
	}

	_.forEach(addItemsList, (item) => {
		const infoItem = {} as ClaimStatusResponse.ILineLevelInformation;
		let claimStatusItem: Fhir.ClaimResponseAddItem = {};
		let eraItem: Fhir.ClaimResponseAddItem = {};
		let manualPaymentItem: Fhir.ClaimResponseAddItem = {};

		infoItem.sequence = item?.sequence;

		const hcpcsCode = getHcpcsCode(item);
		infoItem.HCPCSCode = {
			code: hcpcsCode?.code,
			system: hcpcsCode?.system,
		};

		// revenue code
		const revCode = getRevenueCode(item);
		infoItem.revenueCode = {
			code: revCode?.code,
			system: revCode?.system,
		};
		infoItem.description = revCode?.display || '';

		// TODO: resolve multiple items with same revenue and hcpcs code using itemSequence
		claimStatusItem = _.find(claimStatusData?.addItem, (responseItem) => {
			return getRevenueCode(responseItem)?.code === revCode?.code && getHcpcsCode(responseItem)?.code === hcpcsCode?.code;
		});
		eraItem = _.find(eraData?.addItem, (responseItem) => {
			return getRevenueCode(responseItem)?.code === revCode?.code;
		});
		manualPaymentItem = _.find(manualPaymentData?.addItem, (responseItem) => {
			return getRevenueCode(responseItem)?.code === revCode?.code && getHcpcsCode(responseItem)?.code === hcpcsCode?.code;
		});

		infoItem.diagnosisCode = _.map(item.diagnosisSequence, (diagnosisSequence) => {
			const diagnosisCode = _.find(claimResponseData?.diagnosis, { sequence: diagnosisSequence });
			if (diagnosisCode) {
				return {
					code: _.find(diagnosisCode?.diagnosisCodeableConcept?.coding, { system: fhirSystemMapping['ICD-09'] })?.code,
					system: fhirSystemMapping['ICD-09'],
				};
			}
		});

		infoItem.procedureCode = _.map(item.procedureSequence, (procedureSequence) => {
			const procedureCode = _.find(claimResponseData?.procedure, { sequence: procedureSequence });
			if (procedureCode) {
				return {
					code: _.find(procedureCode?.procedureCodeableConcept?.coding, { system: fhirSystemMapping['SV101-02'] })?.code,
					system: fhirSystemMapping['SV101-02'],
				};
			}
		});

		infoItem.placeOfService = _.find(
			item?.extension,
			(extension) => extension?.url === fhirSystemMapping.placeOfServiceCode,
		)?.valueCode;

		const careTeamSequence = item?.careTeamSequence;
		if (careTeamSequence) {
			infoItem.renderingProvider = _.compact(
				_.map(item?.careTeamSequence, (sequence) => _.get(careTeam?.renderingProvider, sequence, null)),
			)?.[0];
		}

		const emergencyIndicatorExtension = _.find(item?.extension, (extension) => extension?.url === fhirSystemMapping['SV1-109']);
		const epsdtExtension = _.find(item?.extension, (extension) => extension?.url === fhirSystemMapping['SV1-111']);
		const familyPlanExtension = _.find(item?.extension, (extension) => extension.url === fhirSystemMapping['SV1-112']);

		infoItem.emergency = getDisplayOrCode(emergencyIndicatorExtension?.valueCodeableConcept?.coding?.[0]);
		infoItem.familyPlanning = !!(
			getDisplayOrCode(epsdtExtension?.valueCodeableConcept?.coding?.[0]) &&
			getDisplayOrCode(familyPlanExtension?.valueCodeableConcept?.coding?.[0])
		);

		const serviceStartDate = item?.servicedDate || item?.servicedPeriod?.start;
		const serviceEndDate = item?.servicedDate || item?.servicedPeriod?.end;
		infoItem.servicePeriod = {
			start: formatDate(serviceStartDate),
			end: formatDate(serviceEndDate),
		};

		infoItem.traceNumber = _.first(item?.traceNumber)?.value || '';

		infoItem.billedAmount = _.toString(item?.net?.value ?? 0);

		infoItem.nonCoveredAmount = _.find(item?.extension, (ext) => ext.url === fhirSystemMapping['SV2-207'])?.valueMoney;

		infoItem.modifier = extractModifiers(item);

		// units
		infoItem.quantity = _.toString(item?.quantity?.value) || '';

		// getting amount info
		const adjudicationData = claimStatusItem?.adjudication || [];
		_.forEach(adjudicationData, (adjudicationItem) => {
			const categoryCoding = adjudicationItem?.category?.coding;
			const submitAmountData = _.filter(categoryCoding, (categoryCode) => categoryCode.code === codeMapping.benefitAmount);
			// paid amount info
			if (_.size(submitAmountData) > 0) {
				infoItem.paymentAmount = _.toString(adjudicationItem?.amount?.value);
				infoItem.paymentAmountUnits = adjudicationItem?.amount?.currency || '';
			}
			// adding status details
			if (_.size(infoItem.statusDetails) === 0) {
				const extensionData = adjudicationItem?.extension || [];
				_.forEach(extensionData, (extension) => {
					const codingData = extension?.valueCodeableConcept?.coding || [];
					_.forEach(codingData, (codeData) => {
						infoItem.statusDetails = [
							...(infoItem.statusDetails || []),
							{ code: codeData?.code || '', display: codeData?.display || '' },
						];
					});
				});
			}
		});

		const manualAdjudicationData = manualPaymentItem?.adjudication || [];
		const eraAdjudicationData = eraItem?.adjudication || [];

		if (eraAdjudicationData.length > 0) {
			infoItem.adjudication = getAdjudicationData(eraAdjudicationData);
			const reviewOutcomeCodes = _.chain(eraItem?.reviewOutcome?.reason)
				.flatMap((reason) => reason?.coding)
				.filter((coding) => coding?.system === fhirSystemMapping.HealthcareRemarkCode)
				.map((coding) => coding?.code)
				.value();

			infoItem.reviewOutcome = reviewOutcomeCodes;
			infoItem.paymentStatus = _.get(eraItem, 'status.coding.0.code');
		}

		if (manualAdjudicationData.length > 0) {
			infoItem.adjudication = getAdjudicationData(manualAdjudicationData);
			const reviewOutcomeCodes = _.chain(manualPaymentItem?.reviewOutcome?.reason)
				.flatMap((reason) => reason?.coding)
				.filter((coding) => coding?.system === fhirSystemMapping.HealthcareRemarkCode)
				.map((coding) => coding?.code)
				.value();

			infoItem.reviewOutcome = reviewOutcomeCodes;
			infoItem.paymentStatus = _.get(manualPaymentItem, 'status.coding.0.code');
		}
		infoList.push(infoItem);
	});

	return infoList;
};

export {
	getResourceDetails,
	getTrnNumber,
	getAdjudicationItems,
	getTotalAmount,
	getPaymentDetails,
	getLineLevelInformation,
	renderAmount,
	getPaymentDetailsFromAllocation,
	CLAIM_STATUS_CODING_SYSTEMS,
};
